import { useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { LogIn, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { registriereReauthHandler } from '@/lib/reauth';

const POLL_INTERVALL_MS = 1500;
// Obergrenze fuer einen Anmeldeversuch. Danach hoert das Polling auf; der
// Dialog bleibt offen, damit es der Nutzer erneut versuchen kann.
const MAX_WARTEN_MS = 5 * 60_000;

/**
 * Prueft ohne apiFetch, ob wieder eine Sitzung besteht — apiFetch wuerde
 * bei 401 erneut den Reauth-Ablauf anstossen und sich selbst im Kreis
 * drehen.
 */
async function sitzungBesteht(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me.php', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Faengt abgelaufene Sitzungen ab (S-1). Statt eines harten Redirects
 * — der jede ungespeicherte Eingabe vernichtet hat — erscheint dieser
 * Dialog. Die Anmeldung laeuft in einem Popup, der geoeffnete View
 * bleibt unangetastet stehen.
 */
export function ReauthDialog() {
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [popupBlockiert, setPopupBlockiert] = useState(false);
  const qc = useQueryClient();

  // Das Promise, auf das der fetch-Wrapper wartet.
  const aufloesenRef = useRef<((ok: boolean) => void) | null>(null);
  const popupRef = useRef<Window | null>(null);
  const timerRef = useRef<number | null>(null);

  const aufraeumen = useCallback((): void => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (popupRef.current !== null && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  const beenden = useCallback(
    (erfolgreich: boolean): void => {
      aufraeumen();
      setLaeuft(false);
      setPopupBlockiert(false);
      setOffen(false);
      const aufloesen = aufloesenRef.current;
      aufloesenRef.current = null;
      if (erfolgreich) {
        // Frische Daten holen; die wiederholte Anfrage laeuft im
        // fetch-Wrapper ohnehin schon.
        void qc.invalidateQueries();
      }
      aufloesen?.(erfolgreich);
    },
    [aufraeumen, qc]
  );

  useEffect(() => {
    return registriereReauthHandler(
      () =>
        new Promise<boolean>((resolve) => {
          aufloesenRef.current = resolve;
          setOffen(true);
        })
    );
  }, []);

  // Verschwindet diese Komponente waehrend ein Versuch laeuft, muss das
  // Promise trotzdem aufgeloest werden. Sonst warten saemtliche
  // apiFetch-Aufrufe fuer immer und die Oberflaeche bleibt im Spinner
  // stehen, ohne dass irgendetwas nachkommt (Befund 42).
  useEffect(() => {
    return () => {
      aufraeumen();
      const aufloesen = aufloesenRef.current;
      aufloesenRef.current = null;
      aufloesen?.(false);
    };
  }, [aufraeumen]);

  function pollingStarten(): void {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    const beginn = Date.now();

    timerRef.current = window.setInterval(() => {
      void (async () => {
        if (await sitzungBesteht()) {
          beenden(true);
          return;
        }

        // Ein geschlossenes Popup beendet das Polling NICHT sofort. Die
        // Pruefung oben kann losgeschickt worden sein, bevor der Callback
        // das Cookie gesetzt hat; wer hier abbraeche, verpasste die
        // Sitzung um Haaresbreite und liesse den Dialog tot zurueck.
        // Stattdessen laeuft es kurz weiter und gibt erst dann auf.
        if (popupRef.current !== null && popupRef.current.closed) {
          setLaeuft(false);
        }

        // Harte Obergrenze, damit der Timer nicht ewig weiterlaeuft.
        if (Date.now() - beginn > MAX_WARTEN_MS) {
          aufraeumen();
          setLaeuft(false);
        }
      })();
    }, POLL_INTERVALL_MS);
  }

  function anmelden(): void {
    setPopupBlockiert(false);
    const popup = window.open(
      '/api/auth/login.php?popup=1',
      'tracking-engel-login',
      'width=520,height=680'
    );
    if (popup === null) {
      // Popup-Blocker: der Nutzer bekommt stattdessen einen Link, der
      // dieselbe Anmeldung in einem neuen Tab oeffnet.
      setPopupBlockiert(true);
      return;
    }
    popupRef.current = popup;
    setLaeuft(true);
    pollingStarten();
  }

  return (
    <Dialog.Root open={offen} onOpenChange={(o) => !o && beenden(false)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-ink/40" />
        <Dialog.Content
          role="alertdialog"
          aria-describedby={undefined}
          className="dialog-mobil-vollbild fixed left-1/2 top-1/2 z-[70] w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <Dialog.Title className="font-serif text-lg text-ink">
            Sitzung abgelaufen
          </Dialog.Title>
          <p className="mt-2 text-sm text-ink-soft">
            Die Anmeldung ist nicht mehr gültig. Melde dich neu an — deine Eingaben auf
            dieser Seite bleiben erhalten.
          </p>

          {popupBlockiert && (
            <p className="mt-3 flex items-start gap-1.5 text-sm text-coral" role="alert">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                Das Anmeldefenster wurde blockiert.{' '}
                <a
                  href="/api/auth/login.php?popup=1"
                  target="_blank"
                  rel="noopener"
                  onClick={() => {
                    setLaeuft(true);
                    pollingStarten();
                  }}
                  className="underline hover:text-akzent"
                >
                  In neuem Tab anmelden
                </a>
              </span>
            </p>
          )}

          {laeuft && (
            <p className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
              <Spinner />
              Warte auf die Anmeldung im anderen Fenster …
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => beenden(false)}>
              Später
            </Button>
            <Button variant="primary" onClick={anmelden} disabled={laeuft}>
              <LogIn className="h-4 w-4" />
              Neu anmelden
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
